import { schedule } from '@ember/runloop';
import { cached, tracked } from '@glimmer/tracking';

import type { BlockVisualization } from 'webapp/enums/enums';
import { BlockRenderStep } from 'webapp/enums/enums';
import {
	getHeightWithMargins,
	getHeightWithoutPaddings,
	getPreciseHeight,
	getWidthWithoutPaddings,
} from 'webapp/utils/html';

import type Block from 'webapp/models-custom/block';
import Serviced from 'webapp/models-custom/serviced';

// Used to control render flow on blocks
// so the elements (title, number, chart, etc) keep proper render order
// Render order is important for proper sizing and positioning of elements
// e.g. title height is needed to calculate the space left for the chart
// Also takes care of triggering a new render when size/data/whatever has changed
// This model is passed through all block's components,
// where they can subscribe to changes and react accordingly
// e.g. on blockInnerHeight change, Block::Content::Chart component might need to rerender its chart to stretch
// Works with triggering events and handling event subscriptions
// Read more: https://databox.atlassian.net/wiki/x/AgAb3Q

type ListenerFn = (changes: RenderChanges) => Promise<void>;

interface EventListenerBinding {
	context: object; // the registering component: identifies the listener, and is `this` for listenerFn
	listenerFn: ListenerFn;
}

type EventListenerList = Partial<Record<BlockRenderStep, EventListenerBinding[]>>;

interface Change<T> {
	old: T;
	new: T;
}

export interface RenderChangesFromData {
	visSettingsHash?: Change<number>;
	visualization?: boolean; // always true if key present
	data?: boolean; // always true if key present
}

interface RenderChanges extends RenderChangesFromData {
	blockInnerHeight?: Change<number>;
	blockInnerWidth?: Change<number>;
	blockFontSize?: Change<number>;
	titleHeight?: Change<number>;
	numbersHeight?: Change<number>;
	font?: boolean; // always true if key present
}

export default class BlockRenderer extends Serviced {
	block: Block;
	node: HTMLElement; // .preview-block
	isWallView: boolean;

	@tracked blockInnerHeight = 0;
	@tracked blockInnerWidth = 0;
	@tracked blockFontSize = 0;
	@tracked titleHeight = 0;
	@tracked numbersHeight = 0;
	renderedVisualizationSettingsHash = 0;
	renderedVisualizationType: BlockVisualization | null = null;

	// this flag is set when we have data to render (sampledata is present)
	// is either set when data is updated on the fly with render events, or on initializing
	// by using this, we optimize the rendering of highcharts (not initializing until is ready)
	// and avoid costly re-renders or size changes on empty charts
	dataReadyForDChart = false;

	constructor(block: Block, node: HTMLElement, isWallView: boolean) {
		super();
		this.block = block;
		this.node = node;
		this.isWallView = isWallView;

		this.block.blockSetup.visualization.addRendererReference();

		schedule('afterRender', this, () => {
			this.getRenderChanges();
		});
	}

	get visualizationSettingsHash() {
		return this.block.blockSetup.visualization.visualizationSettingsHash;
	}

	get visualizationType() {
		return this.block.visualization;
	}

	registerStepHandler(stepName: BlockRenderStep, context: object, listenerFn: ListenerFn): void {
		this.bindListener(stepName, context, listenerFn);
	}

	unregisterStepHandler(stepName: BlockRenderStep, context: object): void {
		this.unbindListener(stepName, context);
	}

	rerender(isOnFontLoad = false, isDataChange = false): Promise<void> {
		// check if we need to update
		const changes = this.getRenderChanges();
		if (isOnFontLoad) {
			changes.font = true;
		}
		if (isDataChange) {
			changes.data = true;
		}

		if (Object.keys(changes).length) {
			if (changes.data || changes.visSettingsHash || changes.visualization) {
				this.updateVisualizationDataCache();
			}

			const firstStep = this.renderSteps[0]!;
			return this.triggerStep(firstStep, changes);
		}

		return Promise.resolve();
	}

	updateVisualizationDataCache() {
		this.block.blockSetup.visualization.updateDataCache();
		this.dataReadyForDChart = true;
	}

	updateBlockInnerSize() {
		const blockNode = this.getBlockNode();

		// block inner height
		const height = getHeightWithoutPaddings(blockNode);
		if (this.blockInnerHeight !== height) {
			this.blockInnerHeight = height;
		}

		// block inner width
		const width = getWidthWithoutPaddings(blockNode);
		if (this.blockInnerWidth !== width) {
			this.blockInnerWidth = width;
		}
	}

	updateTitleHeight(): RenderChanges['titleHeight'] {
		if (!this.node.isConnected) {
			return;
		}

		const titleNode = this.node.querySelector<HTMLElement>('.bc__title')!;

		// title height
		const oldValue = this.titleHeight,
			value = Math.ceil(getHeightWithMargins(titleNode)); // assume max value if float
		if (oldValue !== value) {
			const changes = {
				old: oldValue,
				new: value,
			};
			this.titleHeight = value;
			return changes;
		}
		return;
	}

	updateNumbersHeight(): RenderChanges['numbersHeight'] {
		if (!this.node.isConnected) {
			return;
		}

		const numbersNode = this.node.querySelector<HTMLElement>('.bc__top-numbers')!;

		// numbers height
		const oldValue = this.numbersHeight,
			value = Math.ceil(getHeightWithMargins(numbersNode)); // assume max value if float
		if (oldValue !== value) {
			const changes = {
				old: oldValue,
				new: value,
			};
			this.numbersHeight = value;
			return changes;
		}
		return;
	}

	getBlockOuterWidth() {
		return this.getBlockNode()?.offsetWidth;
	}

	getTopContentHeight() {
		if (!this.node.isConnected) {
			return 0;
		}

		const topContentNode = this.node.querySelector<HTMLElement>('.block-content__top')!;
		return getPreciseHeight(topContentNode);
	}

	private getRenderChanges(): RenderChanges {
		if (!this.node.isConnected) {
			return {};
		}

		const renderChanges: RenderChanges = {};

		const prevBlockInnerHeight = this.blockInnerHeight,
			prevBlockInnerWidth = this.blockInnerWidth,
			prevTitleHeight = this.titleHeight,
			prevNumbersHeight = this.numbersHeight;

		this.updateBlockInnerSize();
		this.updateTitleHeight();
		this.updateNumbersHeight();

		// block inner height
		if (this.blockInnerHeight !== prevBlockInnerHeight) {
			renderChanges.blockInnerHeight = {
				old: prevBlockInnerHeight,
				new: this.blockInnerHeight,
			};
		}

		// block inner width
		if (this.blockInnerWidth !== prevBlockInnerWidth) {
			renderChanges.blockInnerWidth = {
				old: prevBlockInnerWidth,
				new: this.blockInnerWidth,
			};
		}

		// block fontsize
		const fontSize = parseFloat(this.node.style.fontSize) || 10;
		if (this.blockFontSize !== fontSize) {
			renderChanges.blockFontSize = {
				old: this.blockFontSize,
				new: fontSize,
			};
			this.blockFontSize = fontSize;
		}

		// title height
		if (this.titleHeight !== prevTitleHeight) {
			renderChanges.titleHeight = {
				old: prevTitleHeight,
				new: this.titleHeight,
			};
		}

		// numbers height
		if (this.numbersHeight !== prevNumbersHeight) {
			renderChanges.numbersHeight = {
				old: prevNumbersHeight,
				new: this.numbersHeight,
			};
		}

		// visualization
		const visualizationType = this.visualizationType;
		if (this.renderedVisualizationType !== visualizationType) {
			renderChanges.visualization = true;
			this.renderedVisualizationType = visualizationType;
		}

		// visualization settings version string
		const visSettingsHash = this.visualizationSettingsHash;
		if (this.renderedVisualizationSettingsHash !== visSettingsHash) {
			renderChanges.visSettingsHash = {
				old: this.renderedVisualizationSettingsHash,
				new: visSettingsHash,
			};
			this.renderedVisualizationSettingsHash = visSettingsHash;
		}

		return renderChanges;
	}

	getHeightForNumbersAndContent(): number {
		return this.blockInnerHeight - this.getTopContentHeight() - this.titleHeight;
	}

	private getBlockNode() {
		return this.node.querySelector<HTMLElement>('.block');
	}

	// --------- Event listener logic ----------

	private listeners: EventListenerList = {};

	private bindListener(stepName: BlockRenderStep, context: object, listenerFn: ListenerFn): void {
		if (!this.listeners[stepName]) {
			this.listeners[stepName] = [];
		}

		this.listeners[stepName].push({
			context,
			listenerFn,
		});
	}

	private unbindListener(stepName: BlockRenderStep, context: object): void {
		if (!this.listeners[stepName]) {
			return;
		}

		this.listeners[stepName] = this.listeners[stepName].filter(
			(item) => item.context !== context,
		);

		if (!this.hasListeners(stepName)) {
			delete this.listeners[stepName];
		}
	}

	private hasListeners(stepName: BlockRenderStep): boolean {
		const listeners = this.listeners[stepName];
		return !!listeners && listeners.length > 0;
	}

	private notifyStepCompletion(stepName: BlockRenderStep, changes: RenderChanges): Promise<void> {
		// listeners can decide by themselves if they will do their rerender logic or not

		if (!stepName) {
			return Promise.resolve();
		}

		const nextStep = this.renderSteps[this.renderSteps.indexOf(stepName) + 1];

		if (!nextStep) {
			return Promise.resolve();
		}

		return this.triggerStep(nextStep, changes);
	}

	private triggerStep(stepName: BlockRenderStep, changes: RenderChanges): Promise<void> {
		// changes can be modified (keys added) by the listeners

		const listenerPromises = (this.listeners[stepName] || []).map((listenerData) => {
			return listenerData.listenerFn.call(listenerData.context, changes);
		});

		const thisStepCompletedPromise = Promise.allSettled(listenerPromises);

		return thisStepCompletedPromise.then(() => {
			return this.notifyStepCompletion(stepName, changes);
		});
	}

	@cached
	private get renderSteps(): BlockRenderStep[] {
		const steps: BlockRenderStep[] = [];

		// top line - DR selector (currenly unnecessary, check on cleanup)
		steps.push(BlockRenderStep.TOP);

		// title - updates titleHeight (bcos can be either 1 or 2 rows)
		steps.push(BlockRenderStep.TITLE);

		// pre-numbers - for multi-numbers, updates the pairs' data and prepares grid
		steps.push(BlockRenderStep.PRE_NUMBERS);

		// sizing of the top numbers for autofit, and multi-number mini-chart resize
		steps.push(BlockRenderStep.SINGLE_NUMBER);

		// post-numbers - updates numbersHeight (so we know how much space is left for chart)
		steps.push(BlockRenderStep.POST_NUMBERS);

		// chart - update / resize
		steps.push(BlockRenderStep.CHART);

		// content - table multicolumns / text resize to fit
		steps.push(BlockRenderStep.CONTENT);

		return steps;
	}

	// eslint-disable-next-line ember/classic-decorator-hooks
	destroy(): void {
		this.block?.blockSetup?.visualization?.removeRendererReference();
		this.listeners = {};
	}
}
